import { Box, Flex, HStack } from '@chakra-ui/react';
import { useToast } from 'hooks';
import { Modal, ModalBody, Button, Progress, ModalHeader } from 'components/UI';
import ProductField from '../ProductField';

type UpdateZipFileProps = {
  onCancelUploadClick(arg0: boolean): void;
  onUpload(zipFile: File, bucket: string): Promise<void>;
  onChange(zipFile?: File): void;
  loading?: boolean;
  displayName: string;
  title: string;
  data: string;
  bucket: string;
  error?: string;
  fileLimit: number;
  progress: number;
  zipFile: File | null;
};

const UpdateZipFile = ({
  onCancelUploadClick,
  onChange,
  loading,
  onUpload,
  displayName,
  title,
  data,
  bucket,
  error,
  fileLimit,
  progress,
  zipFile,
}: UpdateZipFileProps) => {
  const [showToast] = useToast();

  const onUploadClick = async () => {
    if (zipFile === null) {
      return;
    }

    const fileLimitMessage = fileLimit >= 1000 ? `${fileLimit / 1000}GB` : `${fileLimit}MB`;

    const fileSize = zipFile ? (zipFile as File).size / 1024 / 1024 : 0;

    if (fileSize > fileLimit) {
      showToast({
        status: 'error',
        message: `The file is too large (max file size is ${fileLimitMessage}).`,
      });
      return;
    }

    if (data !== zipFile?.name) {
      showToast({
        status: 'error',
        message: `File name must be the same as original - ${data}`,
      });
      return;
    }

    await onUpload(zipFile as File, bucket);

    onCancelUploadClick(true);
  };

  const renderErrorMessage = error ? <Box color="red">{error}</Box> : null;

  const renderProgress = progress > 0 ? <Progress value={progress} w="full" /> : null;

  const handleChange = (
    _fileStateProp: string,
    newFile: File | undefined,
    _objectNameProp: string,
    _objectName: string
  ) => {
    onChange(newFile);
  };

  return (
    <Modal
      isOpen
      isCloseButton
      closeOnEsc={false}
      closeOnOverlayClick
      isCentered
      size="md"
      onClose={() => onCancelUploadClick(true)}
    >
      <ModalHeader
        sx={{
          fontSize: 'md',
          px: 6,
          pt: 4,
          pb: 2,
          fontWeight: 'bold',
          letterSpacing: '0',
        }}
      >
        {title}
      </ModalHeader>
      <ModalBody>
        <Flex flexDir="column">
          <Box
            sx={{
              bgColor: 'base2Colour.400',
              mb: 1.5,
              p: 2.5,
            }}
          >
            <Flex
              sx={{
                flex: 1,
                flexDir: 'column',
                justifyContent: 'column',
                bgColor: 'base2Colour.500',
                m: 1.5,
                alignItems: 'space-between',
                fontSize: 'sm',
                color: 'base2FontColour.500',
              }}
            >
              <ProductField
                name="zipFileName"
                displayName={displayName}
                uploadDisabled={loading}
                buttonText="Select"
                edit
                fieldType="fileSelect"
                onChange={handleChange}
                fileStateProp="zipFile"
                data={zipFile?.name}
              />
            </Flex>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
              }}
            >
              {renderProgress}
            </Box>
          </Box>
        </Flex>
        <HStack
          sx={{
            justifyContent: 'center',
            gap: 2,
            mt: 1,
            mb: 2,
          }}
        >
          <Button size="sm" isDisabled={!zipFile?.name} onClick={onUploadClick} isLoading={loading}>
            UPLOAD
          </Button>
          <Button size="sm" onClick={() => onCancelUploadClick(false)}>
            CANCEL
          </Button>
        </HStack>

        {renderErrorMessage}
      </ModalBody>
    </Modal>
  );
};

export default UpdateZipFile;
